import { ProductsService } from 'src/app/_services/products.service';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Paginator } from 'primeng/paginator';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { GlobalService } from 'src/app/_services/global.service';
import { Router } from '@angular/router';
import { FilterServiceService } from 'src/app/_services/filter-service.service';
import { ThisReceiver } from '@angular/compiler';
import { BarcodeLabelPrintingService } from 'src/app/_services/barcode-label-printing.service';
import { isProductVariantFeatureEnabled } from 'src/app/utils/product-variant-feature';
import { finalize } from 'rxjs/operators';
import { BusinessSetupProgressService } from 'src/app/_services/business-setup-progress.service';

@Component({
  selector: 'app-products-list-v2',
  templateUrl: './products-list-v2.component.html',
  styleUrls: ['./products-list-v2.component.scss']
})
export class ProductsComponentListV2 implements OnInit {
  _globals = GlobalService
  readonly showVariantFilter = isProductVariantFeatureEnabled();

  page = 0
  rows = 10
  first = 0
  totalRecords = 0
  doChangePage = true
  selectedRow: any = ''
  selectAll = false

  lastSearchText = ''
  dataList: any = null
  allDataList: any[] = []
  isLoading = false
  skeletonRows: { index: number }[] = []
  /** True once getDataList()'s first successful response has landed — gates the product-limit
   * badge/button-disabling so they don't fire off totalRecords' default 0 before the real count loads. */
  hasLoadedProductsOnce = false
  variantFilter = 'all'
  variantFilterOptions = [
    { label: 'All', value: 'all' },
    { label: 'Simple', value: 'simple' },
    { label: 'Variant', value: 'variant' },
  ]
  showDetail:boolean = false;
  activeProduct:any = null

  /** Allowed-product cap from the merchant's businessplan config (root or businessSetup[0]; null = unlimited/unset). */
  get allowedProductsLimit(): number | null {
    const user = this._globals.userInfo || {};
    const setup = Array.isArray(user.businessSetup) ? user.businessSetup[0] : user.businessSetup;
    const value = user.noOfProducts ?? user.NoOfProducts ?? setup?.noOfProducts ?? setup?.NoOfProducts;
    if (value === undefined || value === null) return null;
    const num = Number(value);
    // 0 is a real, admin-configured cap (zero products allowed) — only negative values
    // (e.g. -1) are the backend's "unlimited/not configured" sentinel.
    return Number.isFinite(num) && num >= 0 ? num : null;
  }

  get productsLimitReached(): boolean {
    return this.hasLoadedProductsOnce && this.allowedProductsLimit !== null && this.totalRecords >= this.allowedProductsLimit;
  }

  /** User-facing phrasing for the usage pill — avoids a confusing "3 of 0" readout when the plan allows zero products. */
  get productsUsageLabel(): string {
    if (this.allowedProductsLimit === 0) {
      return 'Product limit reached';
    }
    if (this.productsLimitReached) {
      return `Product limit reached (${this.allowedProductsLimit} allowed)`;
    }
    return `${this.totalRecords} of ${this.allowedProductsLimit} products used`;
  }
  //order
  orderParam = ''
  orderBy = ''
  uiSortField = ''
  uiSortOrder: 1 | -1 = 1

  colheader = [[
    'Product Name', 'Barcode', 'SKU', 'Type', 'Track Quantity'
    , 'Sell Online', 'Sale Price', 'Last Updated'
  ]];

  colflds = ['itemName', 'barCode', 'skuCode', 'variantType', 'trackStock',
    'sellOnline', 'sellingPrice', 'lastEdited'
  ];

  csvcols = []
  exportColumns: any = ''

  actions: MenuItem[] = [
    { label: 'Edit', icon: 'pi pi-book' },
    { label: 'Delete', icon: 'pi pi-trash' },
  ]

  @ViewChild('p', { static: false }) paginator: Paginator;

  constructor(
    private _router: Router,
    private _productService: ProductsService,
    private _connMessageService: ConnMessageService,
    public _barcodeLabelServices:BarcodeLabelPrintingService,
    private _confirmationService: ConfirmationService,
    private _spinner: NgxSpinnerService,
    private _abd: AppBaseDataService,
    private http: HttpClient,
    private _filterService: FilterServiceService,
    private _businessSetupProgress: BusinessSetupProgressService,
  ) {
    this.csvcols = this._abd.makeCsvCols(this.colheader, this.colflds)
    this.exportColumns = this.csvcols.map(col => ({ title: col.header, dataKey: col.field }))
    this.setSkeletonRows()
  }

  /** Domain & store template only matters for merchants selling online — same signal as
   *  business-setup-checklist.component.ts's domainStepNeeded. Without this check, an in-store-only
   *  merchant (who will never complete that step, correctly) would see this banner forever and
   *  have "Add new product"/"Import" permanently disabled (see [disableAddControls] below). */
  get sellsOnline(): boolean {
    const setup = this._globals.userInfo?.businessSetup?.[0];
    return !!(setup?.standardOnlineStore ?? setup?.StandardOnlineStore);
  }

  /** Restriction disabled: products can be added before the domain & store template is set up. */
  private readonly enforceDomainSetupBeforeProducts = false;

  get showDomainSetupBanner(): boolean {
    return this.enforceDomainSetupBeforeProducts && this.sellsOnline && !this._businessSetupProgress.isDomainTemplateSaved;
  }

  get addProductBlockedMessage(): string {
    if (this.showDomainSetupBanner) {
      return 'Set up your domain & store template before adding products.';
    }
    return `Product limit reached (${this.allowedProductsLimit} allowed).`;
  }

  goToBusinessSetup(): void {
    this._router.navigate(['/app/business-setup'], { queryParams: { section: 'domain' } });
  }

  ngOnInit(): void {
    this._globals.componentName = 'Products'
    this._abd.loadInit().subscribe();
    this._abd.isRedirect = null
  }

  ngAfterViewInit() {
    let ti = null
    ti = setInterval(() => {
      let ppages = this._filterService.getPages(this._globals.componentName)
      if (ppages) {
        let lastpage = parseInt(ppages.page)
        if (lastpage < 0) lastpage = 0
        if (lastpage > 0) {
          this.page = lastpage
          this.first = lastpage * this.rows
        }
      }

      let filter = this._filterService.getFilters(this._globals.componentName)
      if (filter) {
        if (filter.searchText) this._filterService.searchText = filter.searchText
        const rr = this._abd.getProductSellTypeFilter(filter.selectedProductSellTypeFilter)
        if (filter.selectedProductSellTypeFilter) this._filterService.selectedProductSellTypeFilter = rr
        if (filter.selectedCategory) this._filterService.selectedCategory = filter.selectedCategory
        if (filter.selectedDepartment) this._filterService.selectedDepartment = JSON.parse(JSON.stringify(filter.selectedDepartment))
        if (filter.selectedManufacturer) this._filterService.selectedManufacturer = filter.selectedManufacturer
        if (filter.selectedVendor) this._filterService.selectedVendor = filter.selectedVendor
      }

      let oorderby = this._filterService.getOrderby(this._globals.componentName)
      if (oorderby) {
        this.orderParam = oorderby.orderParam
        this.orderBy = oorderby.orderBy
      }

      const outfilter = {
        searchText: this._filterService.searchText ? this._filterService.searchText : '',
        // selectedProductSellTypeFilter: sellOnline == 1 ? 'true' : 'false',
        selectedDepartment: this._filterService.selectedDepartment,
        selectedCategory: this._filterService.selectedCategory,
        selectedVendor: this._filterService.selectedVendor,
        selectedManufacturer: this._filterService.selectedManufacturer,
      }
      this.getInitData()
      clearInterval(ti)
    }, 700);

  }

  ngOnDestroy() {
    this._globals.componentName = ''
  }

  makeParams() {
    //--
    let filter: any = {}
    let params = new HttpParams();

    let locationId: any = null
    let departmentId = null
    let categoryId = null
    let vendorId = null
    let manufactureId = null

    let sellOnline = this._filterService.selectedProductSellTypeFilter?.id;
    if (sellOnline) {
      if (sellOnline == 1)
        params = params.append('sellOnline', 'true')
      else if (sellOnline == 2)
        params = params.append('sellOnline', 'false')
    } else {
      sellOnline = 3
      // params = params.append('sellOnline', 'true')
    }

    try {
      categoryId = this._filterService.selectedCategory?.id.toString()
      locationId = this._filterService.selectedLocation?.id
      departmentId = this._filterService.selectedDepartment?.id.toString()
      vendorId = this._filterService.selectedVendor?.id.toString()
      manufactureId = this._filterService.selectedManufacturer?.id.toString()
    } catch { }

    //if (locationId) params = params.append('locationId', locationId.toString());
    if (departmentId) params = params.append('departId', departmentId.toString());
    if (categoryId) params = params.append('categoryId', categoryId.toString());
    if (vendorId) params = params.append('vendorId', vendorId.toString());
    if (manufactureId) params = params.append('manufactureId', manufactureId.toString());

    this.page = this.page < 1 ? 0 : this.page
    this.rows = this.rows < 1 ? 10 : this.rows

    params = params.append('pageNo', this.page.toString());
    params = params.append('pageSize', this.rows.toString());

    if (this._filterService.searchText?.length > 0) {
      params = params.append('filterValue', this._filterService.searchText);
    }

    //------
    const outfilter = {
      searchText: this._filterService.searchText ? this._filterService.searchText : '',
      selectedProductSellTypeFilter: sellOnline,
      selectedDepartment: this._filterService.selectedDepartment,
      selectedCategory: this._filterService.selectedCategory,
      selectedVendor: this._filterService.selectedVendor,
      selectedManufacturer: this._filterService.selectedManufacturer,
    }
    this._filterService.updateFilters(this._globals.componentName, outfilter)
    this._filterService.updatePages(this._globals.componentName, { page: this.page, rows: this.rows })
    return params
    //--

  }

  getDataList() {
    let hparas = this.makeParams()
    let qryParas = null
    if (this.orderParam && this.orderParam.length > 0) {
      qryParas = `?orderParam=${this.orderParam}&orderBy=${this.orderBy}`
      this._filterService.updateOrderby(this._globals.componentName, { orderParam: this.orderParam, orderBy: this.orderBy })
    }

    this.setSkeletonRows()
    this.isLoading = true
    this._productService.getAllProductList4InventoryV3(hparas, qryParas).pipe(
      finalize(() => {
        this.isLoading = false
      })
    ).subscribe(res => {
      if (res.success) {
        this.totalRecords = res.totalRecords
        this.hasLoadedProductsOnce = true
        this.allDataList = Array.isArray(res.data) ? res.data : []
        this._globals.showProductDelButton = false
        this._barcodeLabelServices.isLabelPrintBtn = false;

        const listToProcess = this.allDataList
        for (let i = 0; i < listToProcess.length; i++) {
          listToProcess[i].selected = false

          try {
            listToProcess[i].imageThumbnail = this.resolveItemThumbnail(listToProcess[i])
          } catch { listToProcess[i].imageThumbnail = '' }

          listToProcess[i].variantType = this.isVariantItem(listToProcess[i]) ? 'Variant' : 'Simple'

          const primarySku = this.getPrimarySku(listToProcess[i])
          if (primarySku) {
            listToProcess[i].barCode = primarySku.barcode ?? primarySku.Barcode ?? primarySku.barCode ?? listToProcess[i].barCode ?? '--'
            listToProcess[i].skuCode = primarySku.skuCode ?? primarySku.SkuCode ?? primarySku.SKUCode ?? listToProcess[i].skuCode ?? '--'
          } else {
            listToProcess[i].barCode = listToProcess[i].barCode ?? '--'
            listToProcess[i].skuCode = listToProcess[i].skuCode ?? '--'
          }
          this.applySalePriceDisplay(listToProcess[i], primarySku)
        }
        this.applyVariantFilter()
        return this.dataList
      }
    })
  }

  setVariantFilter(value: string) {
    this.variantFilter = value
    this.applyVariantFilter()
  }

  private applyVariantFilter() {
    if (!Array.isArray(this.allDataList)) {
      this.dataList = this.allDataList
      return
    }

    let filtered: any[] = []

    if (this.variantFilter === 'simple') {
      filtered = this.allDataList.filter((item) => !this.isVariantItem(item))
      this.dataList = this.applyClientSort(filtered)
      return
    }

    if (this.variantFilter === 'variant') {
      filtered = this.allDataList.filter((item) => this.isVariantItem(item))
      this.dataList = this.applyClientSort(filtered)
      return
    }

    filtered = [...this.allDataList]
    this.dataList = this.applyClientSort(filtered)
  }

  private isVariantItem(item: any): boolean {
    return !!(item?.isVariantProduct ?? item?.IsVariantProduct ?? false)
  }

  private getPrimarySku(item: any): any | null {
    const skus = this.getSkuList(item)
    if (!skus.length) return null

    if (this.isVariantItem(item)) {
      const def = skus.find((sku) => sku?.isDefault ?? sku?.IsDefault)
      if (def) return def
      const visible = skus.find((sku) => sku?.isVisible ?? sku?.IsVisible)
      if (visible) return visible
    }

    return skus[0]
  }

  private getSkuList(item: any): any[] {
    const candidates = [
      item?.crsItemSKUs,
      item?.crsItemSkus,
      item?.CrsItemSKUs,
      item?.CrsItemSkus,
      item?.itemSKUs,
      item?.ItemSKUs,
      item?.SKUs,
      item?.SkUs,
      item?.skUs,
      item?.skus,
      item?.skuList,
      item?.SkuList,
      item?.itemSKUList,
      item?.ItemSKUList,
      item?.itemSkuList,
      item?.variantRows,
      item?.VariantRows,
      item?.variants,
      item?.Variants
    ]
    for (const entry of candidates) {
      if (Array.isArray(entry)) return entry
      if (typeof entry === 'string') {
        try {
          const parsed = JSON.parse(entry)
          if (Array.isArray(parsed)) return parsed
        } catch { }
      }
    }
    return []
  }

  private applySalePriceDisplay(item: any, primarySku: any): void {
    if (this.isVariantItem(item)) {
      const prices = this.getVariantSalePriceAList(item)

      if (prices.length) {
        const sorted = [...prices].sort((a, b) => a - b)
        item.priceMin = sorted[0]
        item.priceMax = sorted[sorted.length - 1]
        item.sellingPrice = item.priceMin
        item.hasPriceRange = item.priceMin !== item.priceMax
        return
      }
    }

    item.sellingPrice = this.getSimpleSkuSalePrice(primarySku, item.sellingPrice) ?? 0
    item.priceMin = item.sellingPrice
    item.priceMax = item.sellingPrice
    item.hasPriceRange = false
  }

  private getVariantSalePriceAList(item: any): number[] {
    const prices = this.getSkuList(item)
      .map((sku) => this.getFirstNumericPrice([
        sku?.salePriceA,
        sku?.SalePriceA,
        sku?.salepricea,
        sku?.salePrice,
        sku?.SalePrice,
        sku?.sellingPrice,
        sku?.SellingPrice,
        sku?.priceA,
        sku?.PriceA,
        sku?.price,
        sku?.Price
      ]))
      .filter((price) => price !== null) as number[]

    const valid = prices.filter((price) => Number.isFinite(price))
    const nonZero = valid.filter((price) => price > 0)
    return nonZero.length ? nonZero : valid
  }

  private getSimpleSkuSalePrice(sku: any, fallback: any = 0): number | null {
    const candidates = [
      sku?.salePriceA,
      sku?.SalePriceA,
      sku?.salepricea,
      fallback,
    ]

    const direct = this.getFirstNumericPrice(candidates)
    if (direct !== null) return direct

    return this.getFirstNumericPrice([
      sku?.salePrice,
      sku?.SalePrice,
      sku?.sellingPrice,
      sku?.SellingPrice,
      fallback,
    ])
  }

  private getFirstNumericPrice(candidates: any[], preferNonZero = false): number | null {
    const parsedPrices: number[] = []

    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined || candidate === '') continue
      const price = this.toPriceNumber(candidate)
      if (price !== null) parsedPrices.push(price)
    }

    if (!parsedPrices.length) {
      return null
    }

    if (preferNonZero) {
      const nonZero = parsedPrices.find((price) => price !== 0)
      if (nonZero !== undefined) {
        return nonZero
      }
    }

    return parsedPrices[0]
  }

  private toPriceNumber(value: any): number | null {
    if (value && typeof value === 'object') {
      const nested = [
        value?.salePriceA,
        value?.SalePriceA,
        value?.value,
        value?.Value,
        value?.amount,
        value?.Amount,
        value?.$numberDecimal,
      ]
      for (const candidate of nested) {
        const parsed = this.toPriceNumber(candidate)
        if (parsed !== null) return parsed
      }
      return null
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null
    }

    const normalized = String(value ?? '').replace(/[^0-9.-]/g, '')
    if (!normalized || normalized === '-' || normalized === '.') {
      return null
    }

    const price = Number(normalized)
    return Number.isFinite(price) ? price : null
  }

  private isDefaultProductImage(url: any): boolean {
    const value = String(url ?? '').trim().toLowerCase()
    if (!value) return true

    return value.includes('defaultproductimage') || value.includes('default_product.png')
  }

  private getFirstRealSkuImage(item: any): string {
    const skus = this.getSkuList(item)
    for (const sku of skus) {
      const skuImage = String(sku?.skuImage ?? sku?.SKUImage ?? sku?.image ?? sku?.Image ?? sku?.imageThumbnail ?? '').trim()
      if (skuImage && !this.isDefaultProductImage(skuImage)) {
        return skuImage
      }
    }
    return ''
  }

  private resolveItemThumbnail(item: any): string {
    const pics = Array.isArray(item?.itemPictures) ? item.itemPictures : []
    const featuredPic = String(pics.find((p: any) => p?.isFeature || p?.isDefault)?.picture ?? '').trim()
    const firstPic = String(pics?.[0]?.picture ?? '').trim()
    const candidates = [
      String(item?.image ?? '').trim(),
      String(item?.imageThumbnail ?? '').trim(),
      featuredPic,
      firstPic
    ].filter((url) => !!url)

    const mainImage = candidates.find((url) => !this.isDefaultProductImage(url)) || candidates[0] || ''

    // Variant rule:
    // If main image is default placeholder, show first real SKU image instead.
    if (this.isVariantItem(item)) {
      if (mainImage && !this.isDefaultProductImage(mainImage)) {
        return mainImage
      }

      const skuImage = this.getFirstRealSkuImage(item)
      if (skuImage) {
        return skuImage
      }
    }

    return mainImage || featuredPic || firstPic || ''
  }


  ExportCompleteInventory() {
    this._abd.showSpinner()
    let params = new HttpParams();
    let locationId = this._abd.actLocations[0].id
    let categoryId = this._filterService.selectedCategory?.id.toString()
      let departmentId = this._filterService.selectedDepartment?.id.toString()
      let vendorId = this._filterService.selectedVendor?.id.toString()
     let manufactureId = this._filterService.selectedManufacturer?.id.toString()
     let sellOnline = this._filterService.selectedProductSellTypeFilter?.Value;
    if (locationId) params = params.append('warehouseId', locationId.toString());
    if (departmentId) params = params.append('departId', departmentId.toString());
    if (categoryId) params = params.append('categoryId', categoryId.toString());
    if (vendorId) params = params.append('vendorId', vendorId.toString());
    if (manufactureId) params = params.append('manufactureId', manufactureId.toString());
    if(sellOnline != 'All'){
        if (sellOnline) params = params.append('sellOnline', sellOnline == "Online" ? true : false);
    }

    this._productService.ExportInventoryCSV(params).subscribe(res => this.downLoadFile(res, "application/csv"))
  }
  /**
       * Method is use to download file.
       * @param data - Array Buffer data
       * @param type - type of the document.
       */
  downLoadFile(data: any, type: string) {
    let blob = new Blob([data], { type: type });
    let url = window.URL.createObjectURL(blob);
    let downloadLink = document.createElement('a');
    downloadLink.href = url

    downloadLink.setAttribute('download', "inventory.csv");

    document.body.appendChild(downloadLink);
    downloadLink.click();

    this._abd.hideSpinner();
  }

  actionButtonClick(item: any) {
    this._productService.activeItem = item
    this._productService.isNewVendor = false
  }

  getInitData() {
    this.getDataList()
  }

  clearSearchText() {
    if (this.lastSearchText?.length > 0) {
      this.lastSearchText = ''
      this.filterButtonPressed()
    }
  }

  searchTextSearch() {
    if (this._filterService.searchText?.length < 1 && this.lastSearchText?.length < 1) return
    this.lastSearchText = this._filterService.searchText //this.searchText
    this.filterButtonPressed()
  }

  onPageChange(e) {
    e.totalRecords = this.totalRecords
    this.rows = e.rows
    this.first = e.first
    this.page = e.page
    this.setSkeletonRows()
    this._abd.setOptionToLocalStorage(this._globals.componentName, 'last-page', e.page)

    this.getDataList()
  }

  selectAllChanged(event) {
    if (event.checked) {
      this._globals.showProductDelButton = true
      this._barcodeLabelServices.isLabelPrintBtn = true;
      for (let i = 0; i < this.dataList.length; i++) {
        this.dataList[i].selected = true
      }
    } else {
      this._globals.showProductDelButton = false
      this._barcodeLabelServices.isLabelPrintBtn = false;
      for (let i = 0; i < this.dataList.length; i++) {
        this.dataList[i].selected = false
      }
    }
  }

  selectionChanged(event) {
    let ret = false
    if (!event.checked) {
      this.selectAll = this.selectAll ? !this.selectAll : this.selectAll
    }

    for (let i = 0; i < this.dataList.length; i++)
      if (this.dataList[i].selected) {
        ret = true
        break
      }

    if (event.checked) {
      const sel = this.dataList.filter(f => f.selected)
      if (sel.length == this.dataList.length) this.selectAll = true
      else this.selectAll = false
    }

    this._globals.showProductDelButton = ret
    this._barcodeLabelServices.isLabelPrintBtn = ret;
  }

  productDelete() {
    this._confirmationService.confirm({
      header: `Delete Product: ${this._productService.activeItem.itemName}?`,
      message: 'Are you sure that you want to perform this action?',
      accept: () => {
        try {
          this._abd.showSpinner()
          this._productService.deleteProduct(this._productService.activeItem.id).pipe(
            finalize(() => {
              this._abd.hideSpinner()
            })
          ).subscribe(res => {
            if (res.success) {
              this.filterButtonPressed()
              this.selectAll = false
              this._globals.showProductDelButton = false
              this._barcodeLabelServices.isLabelPrintBtn = false;
              this._connMessageService.showToastMessage(`Product Deleted Successfully.`, 'success');
            }
          })
        } catch { this._abd.hideSpinner() }
      }
    });
  }

  commaSeperatedIds(data: any): string {
    let selectedItems: string = "";
    let z = data.filter(x => x.selected)
    if (z.length > 100) {
      this._connMessageService.showToastMessage('Cannot delete more than 100 items at a single time.', 'error')
      return null
    }
    z.forEach(x => { selectedItems += x.id + "," })
    selectedItems = selectedItems.slice(0, -1);
    return selectedItems
  }

  deleteMultiple() {
    let marked = this.dataList.filter(f => f.selected)
    let markedIds = this.commaSeperatedIds(marked);
    if (!marked || !markedIds) return

    let header = 'Deleting product Confirmation'
    if (marked.length > 1) header = `Deleting ${marked.length} product(s) Confirmation`

    this._confirmationService.confirm({
      message: 'Deleting process causes permanent loss of information. Are you sure to proceed?',
      header: header,
      icon: 'pi pi-info-circle',
      accept: () => {
        try {
          this._abd.showSpinner()
          this._productService.deleteProduct(markedIds).pipe(
            finalize(() => {
              this._abd.hideSpinner()
            })
          ).subscribe(res => {
            if (res.success) {
              this._connMessageService.showToastMessage(`Deleted ${marked.length} product(s) successfully.`, 'success');
              this.getDataList()
              this.selectAll = false
              this._globals.showProductDelButton = false
              this._barcodeLabelServices.isLabelPrintBtn = false;
            }
          })
        } catch {
          this.getDataList()
          this._abd.hideSpinner()
        }
      },
      reject: () => {
      }
    });
  }
  printLabel(){
    let list = this.dataList.filter(f => f.selected)
    list = list.map((v)=>{
      return{
        productId:v.id,
        productName:v.itemName,
        barcode:v.barCode,
        skuCode:v.skuCode,
        salePriceA:v.sellingPrice,
        locationName:"",
        locationId:"",
      }
    })
    this._barcodeLabelServices.selectedListToPrintLabel = list
    this._barcodeLabelServices.productListPrintBarcodeLabel = true;
    this._globals.showProductDelButton = false
    this._barcodeLabelServices.isLabelPrintBtn = false;
    for (let i = 0; i < this.dataList.length; i++) {
      this.dataList[i].selected = false
    }
  }

  filterButtonPressed() {
    this.page = 0
    this.paginator?.changePage(0)
    this.getDataList()
  }

  callbackFuncFromFilter() {
    this.filterButtonPressed()
  }

  exportExcel() {
    const source = Array.isArray(this.dataList) ? this.dataList : [];
    if (!source.length) {
      this._connMessageService.showToastMessage('No products available to export.', 'error');
      return;
    }

    const exportRows = source.map((item: any) => {
      const minPrice = this.toPriceNumber(item?.priceMin);
      const maxPrice = this.toPriceNumber(item?.priceMax);
      const singlePrice = this.toPriceNumber(item?.sellingPrice) ?? 0;

      const priceValue = item?.hasPriceRange
        ? `${this._globals.formatNumberWithCurrencySign(minPrice ?? 0)} - ${this._globals.formatNumberWithCurrencySign(maxPrice ?? (minPrice ?? 0))}`
        : this._globals.formatNumberWithCurrencySign(singlePrice);

      const lastEditedValue = item?.lastEdited
        ? new Date(item.lastEdited).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric'
        })
        : '';

      return {
        itemName: item?.itemName ?? '',
        barCode: item?.barCode ?? '',
        skuCode: item?.skuCode ?? '',
        variantType: item?.variantType ?? '',
        trackStock: item?.trackStock ? 'Yes' : 'No',
        sellOnline: item?.sellOnline ? 'Yes' : 'No',
        sellingPrice: priceValue,
        lastEdited: lastEditedValue
      };
    });

    this._abd.exportExcel(exportRows, this.colheader[0], this.colflds, 'Product Report');
  }

  exportPdf() {
    let x = this.paginator
    this.paginator.changePage(3)
    //return
    this._abd.exportPdf(this.dataList, this.colheader, this.colflds, 'Product Report')
  }

  redirectToNewProduct(routeOverride?: string[]) {
    this.dataList = ''
    for (let j = 0; j < this._productService.selectedLocations.length; j++) {
      let line = this._productService.selectedLocations[j]
      if (line.stockOnHand) line.stockOnHand = 0
      if (line.openingStock) line.openingStock = 0
      if (line.stockOnHandQoh) line.stockOnHandQoh = line.stockOnHand
    }
    this._router.navigate(routeOverride ?? this.getNewProductRoute())
  }

  navigateToNewProduct() {
    this._router.navigateByUrl('/app/new-product-v3')
  }

  private getNewProductRoute(): string[] {
    return ['app/new-product-v3']
  }

  productViewClicked() {
    this._productService.isNew = false
    this._productService.isEdit = false
    this._productService.isViewOnly = true
    // ensure feature image resets to the selected product for view-only
    this._productService.featureImage = this._productService.activeItem?.image || null
    this._productService.thImages = []
    this._productService.thImageNames = []
    this.redirectToNewProduct()
  }

  productEditClicked() {
    this._productService.isNew = false
    this._productService.isEdit = true
    this._productService.isViewOnly = false
    // reset image state to the active product so feature highlight shows correctly
    this._productService.featureImage = this._productService.activeItem?.image || null
    this._productService.thImages = []
    this._productService.thImageNames = []
    const editRoute = ['app/new-product-v3']
    this.redirectToNewProduct(editRoute)
  }

  onRowSelect(e) {
    this._productService.activeItem = e.data
  }
  onRowUnselect(e) { }

  customSort(e) {
    const sortField = e?.field ?? e?.sortField ?? ''
    const last = this.orderParam
    let nextOrderParam = ''

    switch (sortField) {
      case 'itemName':
        nextOrderParam = 'title'
        break;
      case 'barCode':
        nextOrderParam = 'Barcode'
        break;
      case 'skuCode':
        nextOrderParam = 'SkuCode'
        break;
      case 'lastEdited':
        nextOrderParam = 'LastEdited'
        break;
      default:
        nextOrderParam = ''
    }

    if (nextOrderParam !== '') {
      const incomingOrder = e?.order
      // p-table re-emits sortFunction every time its value changes (each fetch result); ignore re-emits of the sort already applied
      if (sortField === this.uiSortField && incomingOrder === this.uiSortOrder) {
        return
      }
      const nextOrderBy =
        incomingOrder === 1 ? 'asc'
          : incomingOrder === -1 ? 'desc'
            : (this.orderBy == 'asc' ? 'desc' : 'asc')

      if (last === nextOrderParam && this.orderBy === nextOrderBy) {
        return
      }

      this.orderParam = nextOrderParam
      this.orderBy = nextOrderBy
      this.uiSortField = sortField
      this.uiSortOrder = this.orderBy === 'asc' ? 1 : -1

      if (Array.isArray(this.dataList) && this.dataList.length) {
        this.dataList = this.applyClientSort(this.dataList)
      }

      if (last !== this.orderParam && incomingOrder !== 1 && incomingOrder !== -1) {
        this.orderBy = 'asc'
        this.uiSortOrder = 1
      }
      this.getDataList()
    }
  }
  showLogs(){
     if(this._productService.activeItem){
      this.activeProduct = this._productService.activeItem?.itemName
      this.showDetail = true;
     }
  }

  private setSkeletonRows() {
    const count = this.rows > 0 ? this.rows : 10
    this.skeletonRows = Array.from({ length: count }, (_, index) => ({ index }))
  }

  private applyClientSort(source: any[]): any[] {
    if (!Array.isArray(source) || !this.uiSortField) {
      return source
    }

    const direction = this.uiSortOrder === 1 ? 1 : -1
    const getComparable = (item: any): string | number => {
      switch (this.uiSortField) {
        case 'itemName':
          return String(item?.itemName ?? '').toLowerCase()
        case 'barCode':
          return String(item?.barCode ?? '').toLowerCase()
        case 'skuCode':
          return String(item?.skuCode ?? '').toLowerCase()
        case 'lastEdited': {
          const ts = new Date(item?.lastEdited ?? '').getTime()
          return Number.isFinite(ts) ? ts : 0
        }
        default:
          return ''
      }
    }

    return [...source].sort((a, b) => {
      const av = getComparable(a)
      const bv = getComparable(b)
      if (av === bv) return 0
      return av > bv ? direction : -direction
    })
  }
}
